// use this Api as a function to make api calls;
import axiosInstance from '../axios';
import type { CacheRequestConfig } from 'axios-cache-interceptor';
import { API_ROUTES } from '../../enums';
import { VIDEOFEED_TYPE } from '../../enums';
import { ACCESS_TYPE, ACCESS_TYPE_ENUM } from '../../enums/ACCESS_ENUM';
import { SortOptions, SortType } from '../../enums/SORT';

export const getAllFolders = async (data: {
  page: number;
  limit: number;
  search?: string;
  sort?: 'asc' | 'desc';
  type: VIDEOFEED_TYPE;
  sortOption: SortOptions;
  selectedType?: 'all' | 'personal' | 'collaborative';
  collaboratorIds?: string[];
  parentFolderId?: string | number;
}) => {
  const { page, limit, search = '', sort, sortOption, selectedType = 'all', collaboratorIds, parentFolderId } = data;

  let url =
    `${API_ROUTES.FOLDER}?page=${page + 1}&limit=${limit}` +
    `&search=${encodeURIComponent(search)}` +
    `&sort=${sort}` +
    `&sortField=${sortOption}` +
    `&type=${data.type}` +
    `&selectedType=${selectedType}`;

  if (collaboratorIds && collaboratorIds.length > 0) {
    url += `&collaboratorIds=${collaboratorIds.map((id) => encodeURIComponent(id)).join(',')}`;
  }

  if (parentFolderId) {
    url += `&parentFolderId=${encodeURIComponent(String(parentFolderId))}`;
  }

  // TODO wtf ?
  const res = await axiosInstance(url);
  return res;
};

export const postCreateFolder = async (data: { name: string; parentFolderId?: string | number }) => {
  const res = await axiosInstance.post(`${API_ROUTES.FOLDER}`, data);
  return res;
};

export const postCreateCollaborativeFolder = async (data: { name: string; collaboratorEmails: string[]; urlPath: string }) => {
  const res = await axiosInstance.post(`${API_ROUTES.COLLABORATIVE_FOLDER}`, data);
  return res;
};

export const patchUpdateCollaborativeFolder = async (
  data: { name: string; collaboratorEmails: string[]; collaboratorsToRemove: string[]; urlPath: string },
  folderId: string,
) => {
  const res = await axiosInstance.patch(`${API_ROUTES.COLLABORATIVE_FOLDER}/${folderId}`, data);
  return res;
};

export const patchUpdateMyCollaborativeFolder = async (
  data: { collaboratorEmails: string[]; collaboratorsToRemove: string[]; urlPath: string; groupIds: string[] },
  folderId: string,
) => {
  const res = await axiosInstance.patch(`${API_ROUTES.MY_COLLABORATIVE_FOLDER}/${folderId}`, data);
  return res;
};

export const patchFolderAccess = async (data: {
  accessId: string;
  access: ACCESS_TYPE | ACCESS_TYPE_ENUM;
  notify: boolean;
  excluded?: boolean;
  share_scope?: 'single' | 'collection';
  contentUrl: string;
  messageTextForSharedContent: string;
}) => {
  console.log('Inner excluded: ', data.excluded);
  const res = await axiosInstance.patch(`${API_ROUTES.ACCESS}/${data.accessId}`, {
    access: data.access,
    notify: data.notify,
    excluded: typeof data.excluded === 'boolean' ? data.excluded : true,
    ...(data.share_scope ? { share_scope: data.share_scope } : {}),
    contentUrl: data.contentUrl,
    messageTextForSharedContent: data.messageTextForSharedContent,
  });
  return res;
};

export const patchAccessDownloadPermission = async (accessId: string, downloadPermission: boolean) => {
  const res = await axiosInstance.patch(`${API_ROUTES.ACCESS}${API_ROUTES.DOWNLOAD_PERMISSON}/${accessId}`, {
    downloadPermission,
  });
  return res;
};

export const postAccessFolder = async (data: {
  targetType: string;
  targetId?: string;
  user?: string;
  group?: string;
  type: string;
  access: string;
  share_scope?: 'single' | 'collection';
  notify: boolean;
  contentUrl: string;
}) => {
  const res = await axiosInstance.post(`${API_ROUTES.ACCESS}`, {
    targetType: data.targetType,
    targetId: data.targetId,
    recipientId: data.user || data.group,
    recipientType: data.type,
    access: data.access,
    ...(data.share_scope ? { share_scope: data.share_scope } : {}),
    notify: data.notify,
    contentUrl: data.contentUrl,
  });
  return res;
};

export const postArrayOfNewAccesses = async (
  data: {
    targetType: string;
    targetId?: string;
    user?: string;
    group?: string;
    organization?: string;
    allowDownloads?: boolean;
    excluded?: boolean;
    type: string;
    access?: string;
    share_scope?: 'single' | 'collection';
    notify: boolean;
    contentUrl: string;
    messageTextForSharedContent: string;
  }[],
) => {
  const res = await axiosInstance.post(
    `${API_ROUTES.ACCESS_MANY}`,
    data.map((obj) => ({
      targetType: obj.targetType,
      targetId: obj.targetId,
      excluded: obj.excluded,
      allowDownloads: obj.allowDownloads,
      recipientId: obj.user || obj.group || obj.organization,
      recipientType: obj.type,
      access: obj.access === ACCESS_TYPE_ENUM.NO_ACCESS ? ACCESS_TYPE_ENUM.WITHOUT_ACCESS : obj.access,
      ...(obj.share_scope ? { share_scope: obj.share_scope } : {}),
      notify: obj.notify,
      contentUrl: obj.contentUrl,
      messageTextForSharedContent: obj.messageTextForSharedContent,
    })),
  );
  return res;
};

export const editFolderName = async (name: string, id: string) => {
  const res = await axiosInstance.patch(`${API_ROUTES.FOLDER}${id}`, {
    name,
  });
  return res;
};

export const uploadFolderThumbnail = async (id: string, data: FormData) => {
  return await axiosInstance.patch(`${API_ROUTES.FOLDER}${id}/thumbnail`, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const removeFolderThumbnail = async (id: string) => {
  return await axiosInstance.delete(`${API_ROUTES.FOLDER}${id}/thumbnail`);
};

export const deleteFolderById = async (folderId: string) => {
  const res = await axiosInstance.delete(`${API_ROUTES.FOLDER}${folderId}`);
  return res;
};

export const deleteFolderIfEmpty = async (folderId: string) => {
  return axiosInstance.delete<{ deleted: boolean }>(`${API_ROUTES.FOLDER}${folderId}/if-empty`);
};

export const getFolderDeleteImpact = async (folderId: string) => {
  const res = await axiosInstance.get(
    `${API_ROUTES.FOLDER}${folderId}/delete-impact`,
  );
  return res;
};

export const getFolderDeleteImpactFromAllContentPage = async (folderId: string) => {
  const res = await axiosInstance.get(
    `${API_ROUTES.FOLDER_ALL_CONTENT}/${folderId}/delete-impact`,
  );
  return res;
};

export const patchMoveFolders = async (
  folderIds: string[],
  destId?: string | null,
  urlPath?: string,
  sharedUrlPath?: string,
) => {
  const res = await axiosInstance.patch(`${API_ROUTES.FOLDER}move`, {
    folderIds,
    destId,
    urlPath,
    sharedUrlPath,
  });
  return res;
};

export const deleteCollaborativeFolderById = async (folderId: string) => {
  const res = await axiosInstance.delete(`${API_ROUTES.COLLABORATIVE_FOLDER}/${folderId}`);
  return res;
};

export const deleteFolderByIdFromAllContentPage = async (folderId: string) => {
  const res = await axiosInstance.delete(`${API_ROUTES.FOLDER_ALL_CONTENT}/${folderId}`);
  return res;
};

export const getFolderById = async (folderId: string) => {
  const res = await axiosInstance(`${API_ROUTES.FOLDER_EXTENDED}/${folderId}`);
  return res;
};

export const getFolderAccessessById = async (folderId: string, includeInherited = false) => {
  const res = await axiosInstance(`${API_ROUTES.FOLDER_ACCESS}/${folderId}${includeInherited ? '?includeInherited=true' : ''}`);
  return res;
};

export const getContentsSharedWithMe = async (
  page: number,
  limit: number,
  sort: SortType,
  search = '',
  sortOption: SortOptions,
  accessType?: ACCESS_TYPE_ENUM[] | null,
) => {
  const res = await axiosInstance.get(
    `${API_ROUTES.CONTENT_SHARED_WITH_ME}?page=${page + 1}&limit=${limit}&search=${search}&sort=${sort}&sortField=${sortOption}${
      accessType ? `&accessType=${JSON.stringify(accessType)}` : ''
    }`,
  );
  return res;
};

export const getShallowFolder = async (id: string) => {
  return await axiosInstance.get(`${API_ROUTES.FOLDER_SHALLOW}/${id}`);
};

export const patchMoveVideofeed = async (
  sourceId: string,
  destId: string,
  videofeedId: string,
  videofeedIds?: string[],
  urlPath?: string,
  sharedUrlPath?: string,
) => {
  const res = await axiosInstance.patch(API_ROUTES.VIDEOFEED_MOVE, {
    sourceId,
    destId,
    videofeedId,
    ...(videofeedIds?.length ? { videofeedIds } : {}),
    ...(urlPath ? { urlPath } : {}),
    ...(sharedUrlPath ? { sharedUrlPath } : {}),
  });
  return res;
};

export const patchMoveVideofeeds = async (
  sourceId: string,
  destId: string,
  moves: Array<{
    videofeedId: string;
    videofeedIds?: string[];
    urlPath?: string;
    sharedUrlPath?: string;
  }>,
) => {
  const first = moves[0];
  return axiosInstance.patch(API_ROUTES.VIDEOFEED_MOVE, {
    sourceId,
    destId,
    videofeedId: first?.videofeedId,
    moves,
    urlPath: first?.urlPath,
    sharedUrlPath: first?.sharedUrlPath,
  });
};

export const patchCopyVideofeed = async (
  sourceId: string,
  destId: string,
  videofeedId: string,
  copyScope?: 'timeline' | 'collection-video',
  videofeedIds?: string[],
) => {
  const res = await axiosInstance.patch(API_ROUTES.VIDEOFEED_COPY, {
    sourceId,
    destId,
    videofeedId,
    ...(copyScope ? { copyScope } : {}),
    ...(videofeedIds?.length ? { videofeedIds } : {}),
  });
  return res;
};

export const postFolderViewRequest = async (id: string) => {
  return await axiosInstance.get(`${API_ROUTES.POST_VIEW_FOLDER}/${id}`);
};

export const getFoldersByUserId = async (userId: string, org_id?: string) => {
  const orgIdQuery = org_id ? `?org_id=${org_id}` : '';
  return await axiosInstance.get(`${API_ROUTES.FOLDERS_BY_USER_ID}/${userId}${orgIdQuery}`);
};

export const getFoldersByUserIdAll = async (userId: string) => {
  return await axiosInstance.get(`${API_ROUTES.FOLDERS_BY_USER_ID}/${userId}`);
};

export const getFoldersByUserIdLightweight = async (userId: string, org_id?: string) => {
  const orgIdQuery = org_id ? `?org_id=${org_id}` : '';
  return await axiosInstance.get(`${API_ROUTES.LIGHTWEIGHT_FOLDERS_BY_USER_ID}/${userId}${orgIdQuery}`);
};

export const getContentsSharedWithMeFoldersComposers = async (
  page: number,
  limit: number,
  sort: SortType,
  search = '',
  sortOption: SortOptions,
  accessType?: ACCESS_TYPE_ENUM[] | null,
) => {
  const res = await axiosInstance.get(
    `${API_ROUTES.CONTENT_SHARED_WITH_ME_FOLDERS_COMPOSERS}?page=${
      page + 1
    }&limit=${limit}&search=${search}&sort=${sort}&sortField=${sortOption}${accessType ? `&accessType=${JSON.stringify(accessType)}` : ''}`,
  );
  return res;
};

export const getAllContentByOrgId = async (
  organizationId: number,
  page: number,
  limit: number,
  sort: SortType,
  sortField: SortOptions,
  search: string,
  filterBy: ACCESS_TYPE_ENUM[] | null,
  type: string | null,
  userIds: string[] | null,
) => {
  return await axiosInstance.get(
    `${API_ROUTES.ALL_CONTENT}/${organizationId}?page=${page + 1}&limit=${limit}&sort=${sort}&sortField=${sortField}&search=${search}${
      filterBy ? `&accessType=${JSON.stringify(filterBy)}` : ''
    }&recordType=${type}${userIds ? `&userIds=${JSON.stringify(userIds)}` : ''}`,
  );
};

export const patchUpdateOrgCollaborators = async (data: { orgCollaborators: number[]; groupIds: number[] }, folderId: number) => {
  const res = await axiosInstance.patch(`${API_ROUTES.FOLDER}${folderId}/org-collaborators`, data);
  return res;
};

export const getFolderCopyStatus = async (folderId: string) => {
  return await axiosInstance.get(`${API_ROUTES.FOLDER_COPY_STATUS}/${folderId}`);
};

export const haveCollaborativeFolders = async (): Promise<boolean> => {
  const response = await axiosInstance.get<{ haveCollaborativeFolders: boolean }>(API_ROUTES.ORGANIZATION_HAVE_COLL_FOLDERS);
  return response.data.haveCollaborativeFolders;
};

export const getMyFolderCollaborators = async () => {
  return await axiosInstance.get(API_ROUTES.MY_FOLDER_COLLABORATORS);
};

export const getListArchivedVideofeedsByFolder = async (folderId: string, periodOnly?: boolean) => {
  const periodParam = periodOnly ? '?period=true' : '';
  const cacheConfig: CacheRequestConfig = { cache: false };
  return await axiosInstance.get(`${API_ROUTES.LIST_ARCHIVED_VIDEOFEEDS}/${folderId}${periodParam}`, cacheConfig);
};

export const getListArchivedFolders = async (parentFolderId?: string | null) => {
  const cacheConfig: CacheRequestConfig = { cache: false };
  const query = parentFolderId ? `?parentFolderId=${parentFolderId}` : '';
  return await axiosInstance.get(`${API_ROUTES.LIST_ARCHIVED_FOLDERS}${query}`, cacheConfig);
};

export const restoreArchivedFolder = async (folderId: number) => {
  return await axiosInstance.patch(`${API_ROUTES.FOLDER_RESTORE}/${folderId}`);
};
